import {Selector} from "./dictionaries.js";
import {
    paddingOligoEditor,
    paddingOligoEditor as paddingEditor,
} from "./constants.js";

import {scaleAnnotationSize} from "../editor/utils";

import {
    createVirtualElement,
    setEditorBoundaries,
} from "../oligo/utils.js";

import {annotationPath} from "./linearShape";

import * as crossMapLookup from "../oligo/crossMapLookup";

import Brush from "./linearBrush";

import {createPopper} from '@popperjs/core';

import {select, selectAll} from "d3-selection";

import {drag} from "d3-drag";
import {zoom as scroll} from "d3-zoom";
import {renderVirtualPopper} from "../utils/virtualPopper";

const d3 = {select, selectAll, drag, scroll}


const editorMixins = {
    setData(decodedStructuredData, sourceData) {
        this.initData(decodedStructuredData, sourceData);
        this.setPlacements();

        /**
         * Recompute editor boundaries (after deleting) websocket when offsetScroll past the sequence end.
         */
        if (this.sequenceLength > this.visibleBasesCount && (this.offsetScroll.current + this.visibleBasesCount) > this.sequenceLength) {
            this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)
        }

        this.render();
        this.renderBrush(this.rangeBp.start, this.rangeBp.start, this.offsetScroll.current, this.fontBoxWidth);

        this.rangeBp.end = this.rangeBp.start;
    },

    setPlacements() {
        this.placements = {
            slots: 20,
            editor: {
                main: 40,
                ruler: 0,
                sequence: 45,
                search: 55,
            }
        }
    },

    initEditor($container) {
        this.mainBox = d3.select($container)
            .append("svg")
            .style("width", "100%")
            .style("height", "100%")

        const $mainContainer = document.querySelector(`#${this.$activeContainer.id}`);
        this.$mainBoxContainer = $mainContainer.getBoundingClientRect();

        this.mainContainer = this.mainBox.append("g").attr("transform", `translate(0, 0)`);
        this.slotsContainer = this.mainContainer.append("g").attr("transform", `translate(0, 0)`);
        this.editorContainer = this.mainContainer.append("g").attr("transform", `translate(0, 0)`);
        this.rulerContainer = this.editorContainer.append("g").attr("transform", `translate(0, 0)`);
        this.sequenceContainer = this.editorContainer.append("g").attr("transform", `translate(0, 0)`);
        this.searchContainer = this.editorContainer.append("g").attr("transform", `translate(0, 0)`);

        this.visibleBasesCount = Math.round(this.$mainBoxContainer.width / this.fontBoxWidth - 2);

        if (this.sequenceLength < this.visibleBasesCount) {
            this.visibleBasesCount = this.sequenceLength
        }

        this.brush = new Brush();
        this.brush.init(this.editorContainer, this.$mainBoxContainer);
        this.initBrush();

        this.offsetScroll = {
            current: 0,
            step: 0,
            other: {
                current: 0,
                step: 0
            }
        }

        this.rangeBp = {
            start: 1,
            end: 1
        }

        this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)

        this.addEditorsEvents();

        this.EventsOn();

        document.addEventListener('click', this.hideContextMenu.bind(this));
        const svgElements = document.querySelectorAll("svg");
        svgElements.forEach((el) => {
            el.addEventListener('pointerdown', (e) => {
                this.hideContextMenu()
            });
        })
    },

    addEditorsEvents() {
        this.mainBox
            .on("contextmenu", (event) => {
                event.preventDefault();

                this.setContexMenuEditor(event, "#contextMenuEditor")
            })
            .call(d3.drag()
                .on("start drag end", e => this.eventOnCreateBrush(e))
            )
            .call(d3.scroll()
                .on("start zoom end", e => {
                        if (e.sourceEvent?.type === "wheel" && this.sequenceLength > this.visibleBasesCount) {
                            if (e.sourceEvent && e.type === "zoom") {
                                const step = e.sourceEvent.wheelDelta < 0 ? 1 : -1;
                                this.offsetScroll.current += step;

                                if (this.offsetScroll.current >= 0 && (this.offsetScroll.current + this.visibleBasesCount - 1) < this.sequenceLength) {
                                    this.offsetScroll.step -= step;
                                }

                                this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)
                                this.createTicks();
                                this.updateBases();
                                this.scrollBrush();
                                this.scroll();
                            } else {
                                if (e.type === "start") {
                                    this.hideContextMenu();
                                } else {
                                    this.updateRender();
                                }
                            }
                        }
                    }
                ))
    },

    render() {
        this.editorContainer
            .attr("transform", `translate(0, ${this.placements.editor.main})`);

        this.renderRuler();
        this.renderSequence();

        if (this.settings.hasSlots) {
            this.renderSlots();
        }

        this.enableTooltip();
    },

    renderRuler() {
        this.rulerContainer.select("line").remove();

        this.rulerContainer
            .attr("transform", `translate(0, ${this.placements.editor.ruler})`)
            .append("line")
            .attr("x1", this.fontBoxWidth)
            .attr("y1", 5)
            .attr("x2", this.$mainBoxContainer.width - this.fontBoxWidth / 2)
            .attr("y2", 5)
            .attr("fill", '#000')
            .style("stroke", '#000')
            .style("stroke-width", 1);
        this.createTicks()
    },


    selectRange(data) {
        const from = data.start - paddingEditor;
        const to   = data.end   - paddingEditor;

        const skipBond = data.source === "sequence";
        let start, end;

        const selection = crossMapLookup.lookupSelection(
            this.crossMap,
            { start: from, end: to },
            { from: data.source, to: this.sourceObject, includeBond: !skipBond }
        );

        if (!selection) return;

        start = selection.start;
        end   = selection.end;

        this.rangeBp = {
            start: start + paddingEditor,
            end:   end   + paddingEditor,
        };

        this.brush.setParameters({
            x:     (this.rangeBp.start + this.offsetScroll.step) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start)       * this.fontBoxWidth,
        });
    },

    drawSlot(container, slot, slotLength, slotDirection, slotColor, height) {
        const arrowLength = 6;
        const [start, end, arrowSize, direction] = scaleAnnotationSize(0, slotLength, slotDirection, arrowLength, this.fontBoxWidth, this.fontSizeBase, this.sequenceLength, true);
        const highlightSlot = 4;
        const self = this;

        container
            .append("path")
            .attr("d", annotationPath(0, height, 0, end, direction, arrowSize, this.fontBoxWidth))
            .attr("class", "editor slot")
            .style("opacity", .6)
            .on("contextmenu", event => {
                event.preventDefault();
                event.stopPropagation();
                self.showContextMenu(event, Selector.SLOT_MENU)
                self.EventsTrigger("appendContextMenuSlotEvents", slot);
            })
            .on("mousedown", event => {
                // block parent click
                event.stopPropagation();
            })
            .on("mouseup", event => {
                event.preventDefault();
                event.stopPropagation();

                const downstreamLinkageOffset = slot.downstream_linkage ? -1 : 0;

                const slotRange = crossMapLookup.lookupRange(
                    self.crossMap,
                    { "start": slot.start, "end": slot.end + downstreamLinkageOffset },
                    { "from": "cartoon", "to": self.sourceObject }
                );

                if (!slotRange) return;

                self.rangeBp = {
                    start: slotRange.start + paddingEditor,
                    end: slotRange.end + paddingEditor
                }

                self.renderBrush(self.rangeBp.start, self.rangeBp.end, self.offsetScroll.current, self.fontBoxWidth);

                self.EventsTrigger("selectRange", {
                    start: self.rangeBp.start,
                    end: self.rangeBp.end,
                    source: self.sourceObject
                });
            })

        // edge
        container
            .append("line")
            .attr("class", "slot")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", "100%")
            .style("stroke-dasharray", ("3, 3"));

        // is editable, is public
        if (slotColor) {
            container.append("line")
                .attr("class", "slot " + slotColor)
                .attr("x1", start * this.fontBoxWidth)
                .attr("y1", height + highlightSlot / 2)
                .attr("x2", ((end - start) * this.fontBoxWidth))
                .attr("y2", height + highlightSlot / 2)
                .attr('stroke-width', highlightSlot)
                .style("opacity", 1);
        }
    },

    setContexMenuEditor(event, contextMenuId) {
        if (!this.settings.editable) return;

        let activeItem = "delete";

        const activeEnc = crossMapLookup.findEntryByRange(
            this.crossMap,
            { start: this.rangeBp.start - paddingEditor, end: this.rangeBp.end - paddingEditor },
            this.sourceObject
        );

        if (activeEnc) {
            activeItem = activeEnc.symbol === "|" ? "new" : "modified";
        }
        const editorMainContextMenuItems = document.querySelectorAll(`${contextMenuId} [data-action-group]`)

        editorMainContextMenuItems.forEach(item => {
            item.classList.add("d-none");

            if (item.dataset.actionGroup === activeItem) {
                item.classList.remove("d-none");
                const deleteItem = item.querySelector("[data-change-encoded-data=delete]")
                if (activeEnc && deleteItem) {
                    if (activeEnc.bond) {
                        deleteItem.classList.add("d-none")
                    } else {
                        deleteItem.classList.remove("d-none")
                    }
                }
            }
        })
        this.showContextMenu(event, contextMenuId);

        this.EventsTrigger("appendContextMenuEditorEvents", {
            ...this.rangeBp,
            "source": this.sourceObject
        });
    },

    showContextMenu(event, contextMenu) {
        this.hideContextMenu();
        const srcContent = document.querySelector(`${contextMenu} div`);
        const contentBody = srcContent.cloneNode(true);

        this.$popperElement = document.querySelector(Selector.POPPER_MENU)
        if (!this.$popperElement) {
            this.$popperElement = document.createElement('div');
            this.$popperElement.id = 'popoverContextMenu';
            this.$popperElement.className = 'popover';
            this.$popperElement.innerHTML = `<div class="popover-body p-0"></div>`;
            document.body.appendChild(this.$popperElement);
        }
        const $contextMenuContent = this.$popperElement.querySelector('.popover-body')
        $contextMenuContent.replaceChildren(contentBody);
        this.$popperElement.classList.remove("d-none")

        const virtualElement = createVirtualElement(event.clientX, event.clientY);

        this.$popper = createPopper(virtualElement, this.$popperElement, {
            placement: 'top',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 15],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {boundary: 'viewport'}
                },
                {
                    name: 'flip',
                    options: {fallbackPlacements: ['bottom', 'right', 'left']}
                }
            ],
        });
    },

    setOffsetScroll(data) {
        let position = data.end;
        let indexMap = this.crossMap.findIndex(item => item[data.source].start === (position - paddingEditor))

        indexMap += (indexMap + 1) > (this.crossMap.length - 1) ? 0 : 1
        const startEditor = this.crossMap[indexMap][this.sourceObject].start + paddingEditor;

        const previousOffsetScrollCurrent = this.offsetScroll.current;
        if (!(startEditor > this.boundaries.left && startEditor < this.boundaries.right)) {
            this.offsetScroll.current = startEditor - Math.round(this.visibleBasesCount / 2);
            this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)

            this.offsetScroll.step = this.offsetScroll.step + (previousOffsetScrollCurrent - this.offsetScroll.current);
        }
    },

    slide(data) {
        if (data === "end") {

            this.createTicks();
            this.updateBases();

            this.enableTooltip();
        }
    },

    scroll() {
        this.slotsContainer
            .attr("transform", `translate(${-(this.offsetScroll.current * this.fontBoxWidth)}, ${this.placements.slots})`);
        this.searchContainer
            .attr("transform", `translate(${-(this.offsetScroll.current * this.fontBoxWidth)}, ${this.placements.editor.search})`);
    },

    updateRender() {
        this.renderSlots();

        this.enableTooltip();
    },

    hideContextMenu() {
        if (this.$popper) {
            this.$popper.destroy();
            this.$popper = null;
            this.$popperElement.classList.add('d-none')
        }
    },

    enableTooltip() {
        const elements = document.querySelectorAll('[data-toggle="virtual-popper"]')
        renderVirtualPopper(elements);
    },

    EventsTrigger(action, data) {
        if (this.mediator) {
            this.mediator.trigger(action, data, this)
        }
    },

    EventsOn() {
        if (this.mediator) {
            this.mediator.on("selectRange", "selectRange", this);
            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);
            this.mediator.on("slide", "slide", this);
            this.mediator.on("scroll", "scroll", this);
            this.mediator.on("updateRender", "updateRender", this);
        }
    }
};

const brushMixins = {
    initBrush() {
        const [brush, brushFirstEdge, brushSecondEdge] = this.brush.create(true);

        [brushFirstEdge, brushSecondEdge].forEach(el => {
            el.call(d3.drag()
                .on("start drag end", (event) => this.eventOnDragBrushEdges(event, el))
            )
        });

        brush.call(d3.drag()
            .on("start drag end", (event) => this.eventOnDragBrush(event))
        );
    },

    eventOnCreateBrush(event) {
        if (event.sourceEvent.detail < 2) {
            if (event.sourceEvent.detail < 2) {
                event.sourceEvent.preventDefault();

                let scroll = 0;

                this.brush.onCreateBrush(event, scroll * this.fontBoxWidth)

                let brushSize = this.brush.getParameters();

                if (event.type === "end") {
                    this.onEndEventBrush(brushSize, event);
                } else if (event.type === "drag") {

                } else {
                    this.initBasePair = Math.round(event.x / this.fontBoxWidth);
                }
            }
        }
    },

    eventOnDragBrush(event) {
        if (event.sourceEvent.detail < 2) {
            this.brush.onDrag(event)

            let brushSize = this.brush.getParameters();

            if (event.type === "end") {
                if (event.x === this.brush.getCurrentX()) {
                    brushSize = {
                        x: event.x,
                        width: .1
                    }
                }

                if (this.offsetScroll.current === 0 && brushSize.x < 0) {
                    brushSize = {
                        x: 0,
                        width: brushSize.width + brushSize.x
                    }
                }

                this.onEndEventBrush(brushSize, event)


            } else if (event.type === "drag") {
                if (event.x < (this.fontBoxWidth) || event.x > (this.$mainBoxContainer.width - this.fontBoxWidth)) {
                    const step = event.x < (this.fontBoxWidth) ? -1 : 1;

                    this.offsetScroll.current += step;

                    if (this.offsetScroll.current >= 1 && (this.offsetScroll.current + this.visibleBasesCount - paddingOligoEditor) < this.sequenceLength) {
                        this.offsetScroll.step -= step;
                    }

                    this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)
                    this.createTicks();
                    this.updateBases();
                }
            }
        }
    },

    eventOnDragBrushEdges(event, selector) {
        let step = 0;
        if (event.x < this.fontBoxWidth || event.x > (this.$mainBoxContainer.width - this.fontBoxWidth)) {
            step = event.x < 0 ? 1 : -1;
        }

        if (
            (event.x > this.$mainBoxContainer.width && this.offsetScroll.current === (this.sequenceLength - this.maxBasePairsInAreaView)) ||
            (event.x < 0 && this.offsetScroll.current === 0)
        ) {
            step = 0;
        }

        this.brush.onDragEdges(event, selector, step * this.fontBoxWidth)
        let brushSize = this.brush.getParameters();

        if (event.type === "end") {
            if (event.x < 0 && this.offsetScroll.current === 0 && brushSize.width > this.fontBoxWidth) {
                brushSize = {
                    x: 0,
                    width: selector.attr('class') === "firstEdge" ? this.rangeBp.end * this.fontBoxWidth : this.rangeBp.start * this.fontBoxWidth
                }
            }
            this.onEndEventBrush(brushSize);
        } else if (event.type === "drag") {
            if (event.x < this.fontBoxWidth || event.x > (this.$mainBoxContainer.width - this.fontBoxWidth)) {
                step = event.x < (this.fontBoxWidth) ? -1 : 1;

                this.offsetScroll.current += step;

                if (this.offsetScroll.current >= 1 && (this.offsetScroll.current + this.visibleBasesCount - paddingOligoEditor) < this.sequenceLength) {
                    this.offsetScroll.step -= step;
                }

                this.boundaries = setEditorBoundaries(this.offsetScroll, this.visibleBasesCount, this.sequenceLength)
                this.createTicks();
                this.updateBases();
            }
        }
    },

    hideBrush() {
        this.brush.hide();
    },

    showBrush() {
        this.brush.show();
    },

    onEndEventBrush(brushSize, event) {
        this.alignRangeBp(brushSize, event);

        this.EventsTrigger("selectRange", {
            start: this.rangeBp.start,
            end: this.rangeBp.end,
            source: this.sourceObject
        });

        this.EventsTrigger("setOffsetScroll", {
            start: this.rangeBp.start,
            end: this.rangeBp.end,
            source: this.sourceObject
        });

        this.EventsTrigger("slide", "end");
        this.EventsTrigger("scroll", "scroll");
    },

    renderBrush(start, end, currentOffset, fontBoxWidth) {
        let newBrushSize = {
            x: (start - currentOffset) * fontBoxWidth,
            width: start === end ? .1 : ((end - start) * fontBoxWidth)
        }

        this.brush.setParameters(newBrushSize)
    }
}

export {
    editorMixins,
    brushMixins
}